import React from "react";
import { SnackbarProvider, useSnackbar } from "notistack";
import { Alert, AlertTitle } from "@mui/material";
import _ from "lodash";

// Maximum number of notifications to display at once
const MAX_SNACK = 5;

/**
 * Context to access the snackbar functionality throughout the app
 */
const SnackbarContext = React.createContext(null);

/**
 * Hook to use the snackbar functionality
 * @returns {Object} - Snackbar methods
 */
export const useMessageBox = () => React.useContext(SnackbarContext);

/**
 * Provider component for MessageBox
 * Wrap your app with this component to enable MessageBox functionality
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
export const MessageBoxProvider = ({ children }) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    // Create alert variants with titles
    const createSnackMessage = (message, title, variant) => {
        return (
            <Alert 
                severity={variant} 
                variant="filled" 
                // 添加图标控制，确保只显示一个图标
                icon={false} // 禁用默认图标，让MUI的Alert组件控制图标
            >
                {title && <AlertTitle>{title}</AlertTitle>}
                {message}
            </Alert>
        );
    };

    // Define the MessageBox API
    const messageBox = {
        /**
         * Show a notification with the specified options
         * @param {Object|string} options - Notification options or message string
         * @returns {string} - ID of the snackbar for closing it
         */
        show: (options) => {
            try {
                let settings = {};

                if (typeof options === "string") {
                    settings = {
                        message: options,
                        variant: "info",
                    };
                } else {
                    settings = {
                        message: options.text || "",
                        title: options.title || "",
                        variant: "info",
                        autoHideDuration: options.duration ? options.duration * 1000 : 5000,
                    };
                }

                return enqueueSnackbar(createSnackMessage(settings.message, settings.title, settings.variant), {
                    autoHideDuration: settings.autoHideDuration,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                });
            } catch (e) {
                // ignore
                console.warn("MessageBox: Error showing notification", e);
            }
        },

        /**
         * Show a success notification
         * @param {Object|string} options - Notification options or message string
         * @returns {string} - ID of the snackbar
         */
        success: (options) => {
            try {
                let settings = {};

                if (typeof options === "string") {
                    settings = {
                        message: options,
                        variant: "success",
                    };
                } else {
                    settings = {
                        message: options.text || "",
                        title: options.title || "",
                        variant: "success",
                        autoHideDuration: options.duration ? options.duration * 1000 : 5000,
                    };
                }

                return enqueueSnackbar(createSnackMessage(settings.message, settings.title, settings.variant), {
                    autoHideDuration: settings.autoHideDuration,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                });
            } catch (e) {
                // ignore
                console.warn("MessageBox: Error showing success notification", e);
            }
        },

        /**
         * Show a danger notification (error)
         * @param {Object|string} options - Notification options or message string
         * @returns {string} - ID of the snackbar
         */
        error: (options) => {
            try {
                let settings = {};

                if (typeof options === "string") {
                    settings = {
                        message: options,
                        variant: "error",
                    };
                } else {
                    settings = {
                        message: options.text || "",
                        title: options.title || "",
                        variant: "error",
                        autoHideDuration: options.duration ? options.duration * 1000 : 5000,
                    };
                }

                return enqueueSnackbar(createSnackMessage(settings.message, settings.title, settings.variant), {
                    autoHideDuration: settings.autoHideDuration,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                });
            } catch (e) {
                // ignore
                console.warn("MessageBox: Error showing danger notification", e);
            }
        },

        /**
         * Show a warning notification
         * @param {Object|string} options - Notification options or message string
         * @returns {string} - ID of the snackbar
         */
        warning: (options) => {
            try {
                let settings = {};

                if (typeof options === "string") {
                    settings = {
                        message: options,
                        variant: "warning",
                    };
                } else {
                    settings = {
                        message: options.text || "",
                        title: options.title || "",
                        variant: "warning",
                        autoHideDuration: options.duration ? options.duration * 1000 : 5000,
                    };
                }

                return enqueueSnackbar(createSnackMessage(settings.message, settings.title, settings.variant), {
                    autoHideDuration: settings.autoHideDuration,
                    anchorOrigin: { vertical: "top", horizontal: "right" },
                });
            } catch (e) {
                // ignore
                console.warn("MessageBox: Error showing warning notification", e);
            }
        },

        /**
         * Show an info notification
         * @param {string} message - The message to display
         * @param {string} title - Optional title
         * @returns {string} - ID of the snackbar
         */
        info: (message, title) => {
            return messageBox.show({
                message,
                title,
                type: "info",
            });
        },

        /**
         * Close a specific notification
         * @param {string} key - ID of the notification to close
         */
        close: (key) => {
            closeSnackbar(key);
        },

        /**
         * Close all notifications
         */
        closeAll: () => {
            closeSnackbar();
        },
    };

    return <SnackbarContext.Provider value={messageBox}>{children}</SnackbarContext.Provider>;
};

/**
 * Root provider that combines SnackbarProvider and MessageBoxProvider
 * @param {Object} props - Component props
 * @returns {JSX.Element} - Rendered component
 */
export const RootMessageBoxProvider = ({ children }) => {
    return (
        <SnackbarProvider maxSnack={MAX_SNACK} preventDuplicate>
            <MessageBoxProvider>{children}</MessageBoxProvider>
        </SnackbarProvider>
    );
};

/**
 * MessageBox utility for displaying notifications
 * This is a static API that can be imported and used anywhere
 */
const MessageBox = {
    // Static reference to the messageBox context value
    _messageBox: null,

    // Method to set the messageBox reference
    _setMessageBox: function (messageBox) {
        this._messageBox = messageBox;
    },

    /**
     * Show a notification with the specified options
     * @param {Object|string} options - Notification options or message string
     */
    show: function (options) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        try {
            const message = _.isString(options) ? options : options.text;
            const settings = {};
            if (options.duration) {
                settings.duration = options.duration;
            }
            return this._messageBox.info(message);
        } catch (e) {
            // ignore
            console.warn("MessageBox: Error showing notification", e);
        }
    },

    /**
     * Show a success notification
     * @param {Object|string} options - Notification options or message string
     */
    success: function (options) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        try {
            const message = _.isString(options) ? options : options.text;
            const settings = {};
            if (options.duration) {
                settings.duration = options.duration;
            }
            return this._messageBox.success(message);
        } catch (e) {
            // ignore
            console.warn("MessageBox: Error showing success notification", e);
        }
    },

    /**
     * Show a danger notification (error)
     * @param {Object|string} options - Notification options or message string
     */
    danger: function (options) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        try {
            const message = _.isString(options) ? options : options.text;
            const settings = {};
            if (options.duration) {
                settings.duration = options.duration;
            }
            return this._messageBox.error(message);
        } catch (e) {
            // ignore
            console.warn("MessageBox: Error showing danger notification", e);
        }
    },

    /**
     * Show a warning notification
     * @param {Object|string} options - Notification options or message string
     */
    warning: function (options) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        try {
            const message = _.isString(options) ? options : options.text;
            const settings = {};
            if (options.duration) {
                settings.duration = options.duration;
            }
            return this._messageBox.warning(message);
        } catch (e) {
            // ignore
            console.warn("MessageBox: Error showing warning notification", e);
        }
    },

    /**
     * Show an info notification
     * @param {string} message - The message to display
     * @param {string} title - Optional title
     */
    info: function (message, title) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        return this._messageBox.info(message, title);
    },

    /**
     * Close a specific notification
     * @param {string} key - ID of the notification to close
     */
    close: function (key) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        return this._messageBox.close(key);
    },

    /**
     * Close all notifications
     */
    closeAll: function () {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return;
        }
        return this._messageBox.closeAll();
    },

    /**
     * Show a confirmation dialog
     * @param {string} message - The message to display
     * @param {string} title - Optional title
     * @returns {Promise} - Resolves with true if confirmed, false otherwise
     */
    confirm: function (message, title) {
        if (!this._messageBox) {
            console.warn("MessageBox: Provider not found. Make sure to wrap your app with RootMessageBoxProvider.");
            return Promise.resolve(false);
        }
        return this._messageBox.confirm(message, title);
    },
};

/**
 * Initialize the MessageBox with the context
 * This component should be used inside the RootMessageBoxProvider
 */
export const MessageBoxInitializer = () => {
    const messageBox = useMessageBox();

    React.useEffect(() => {
        // Set the messageBox reference for the static API
        MessageBox._setMessageBox(messageBox);

        return () => {
            // Clean up the reference when unmounted
            MessageBox._setMessageBox(null);
        };
    }, [messageBox]);

    return null;
};

export default MessageBox;
